import React, { useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  LinearProgress,
  Alert,
  Chip,
} from "@mui/material";
import { CloudUpload as CloudUploadIcon } from "@mui/icons-material";
import { Base_Url } from "../../constants/constants";
import useToken from "../Login/useToken";

interface FileUploadStatus {
  file: File | null;
  isUploading: boolean;
  uploadProgress: number;
  success: boolean;
  error: string | null;
}

const VenueDataImportView: React.FC = () => {
  const [fileStatus, setFileStatus] = useState<FileUploadStatus>({
    file: null,
    isUploading: false,
    uploadProgress: 0,
    success: false,
    error: null,
  });
  const { token } = useToken();

  const resetFileStatus = () => {
    setFileStatus({
      file: null,
      isUploading: false,
      uploadProgress: 0,
      success: false,
      error: null,
    });
  };

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file type
      if (!file.name.toLowerCase().endsWith(".csv")) {
        setFileStatus((prev) => ({
          ...prev,
          error: "Please select a CSV file (.csv extension required)",
        }));
        return;
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        setFileStatus((prev) => ({
          ...prev,
          error: "File size must be less than 10MB",
        }));
        return;
      }

      setFileStatus((prev) => ({
        ...prev,
        file,
        error: null,
      }));
    },
    []
  );

  const handleUpload = async () => {
    if (!fileStatus.file) return;

    setFileStatus((prev) => ({
      ...prev,
      isUploading: true,
      uploadProgress: 0,
      error: null,
    }));

    try {
      const formData = new FormData();
      formData.append("file", fileStatus.file);

      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setFileStatus((prev) => ({
            ...prev,
            uploadProgress: progress,
          }));
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200) {
          try {
            const response = JSON.parse(xhr.responseText);
            setFileStatus((prev) => ({
              ...prev,
              isUploading: false,
              success: true,
              uploadProgress: 100,
            }));
          } catch (error) {
            setFileStatus((prev) => ({
              ...prev,
              isUploading: false,
              error: "Invalid response from server",
              uploadProgress: 0,
            }));
          }
        } else {
          // Handle non-200 status codes by updating state instead of throwing
          let errorMessage = `Upload failed with status: ${xhr.status}`;

          // Try to parse error response for more details
          try {
            const errorResponse = JSON.parse(xhr.responseText);
            if (errorResponse.error) {
              errorMessage = errorResponse.error;
            }
          } catch {
            // If we can't parse the response, use the default message
            if (xhr.status === 500) {
              errorMessage = "Internal server error. Please try again later.";
            }
          }

          setFileStatus((prev) => ({
            ...prev,
            isUploading: false,
            error: errorMessage,
            uploadProgress: 0,
          }));
        }
      };

      xhr.onerror = () => {
        setFileStatus((prev) => ({
          ...prev,
          isUploading: false,
          error:
            "Network error during upload. Please check your connection and try again.",
          uploadProgress: 0,
        }));
      };

      xhr.open("POST", `${Base_Url}/venues/import`);
      xhr.setRequestHeader("Authorization", token);
      xhr.send(formData);
    } catch (error) {
      setFileStatus((prev) => ({
        ...prev,
        isUploading: false,
        error: error instanceof Error ? error.message : "Upload failed",
        uploadProgress: 0,
      }));
    }
  };

  const renderFileUploadArea = () => (
    <Paper
      elevation={2}
      sx={{
        p: 4,
        textAlign: "center",
        border: "2px dashed #ccc",
        borderRadius: 2,
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "#f5f5f5",
        },
      }}
    >
      <input
        accept=".csv"
        style={{ display: "none" }}
        id="csv-file-upload"
        type="file"
        onChange={handleFileSelect}
      />
      <label htmlFor="csv-file-upload">
        <CloudUploadIcon sx={{ fontSize: 48, color: "#666", mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Upload CSV File
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Click here to select your venue data CSV file
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Supported format: .csv (max 10MB)
        </Typography>
      </label>
    </Paper>
  );

  const renderSelectedFile = () => {
    if (!fileStatus.file) return null;

    return (
      <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Selected File
        </Typography>

        <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
          <Chip label={`File: ${fileStatus.file.name}`} variant="outlined" />
          <Chip
            label={`Size: ${(fileStatus.file.size / 1024).toFixed(1)} KB`}
            variant="outlined"
          />
        </Box>
      </Paper>
    );
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: "auto" }}>
      <Typography variant="h4" gutterBottom>
        Venue Data Import
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Upload a CSV file containing venue data to import into the system.
      </Typography>

      {/* Error Alert */}
      {fileStatus.error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={resetFileStatus}>
              Reset
            </Button>
          }
        >
          {fileStatus.error}
        </Alert>
      )}

      {/* Success Alert */}
      {fileStatus.success && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={resetFileStatus}>
              Upload Another
            </Button>
          }
        >
          File uploaded successfully!
        </Alert>
      )}

      {/* File Upload Area */}
      {!fileStatus.file && !fileStatus.success && renderFileUploadArea()}

      {/* Selected File Display */}
      {renderSelectedFile()}

      {/* Upload Progress */}
      {fileStatus.isUploading && (
        <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Uploading...
          </Typography>
          <LinearProgress
            variant="determinate"
            value={fileStatus.uploadProgress}
            sx={{ mb: 1 }}
          />
          <Typography variant="body2" color="text.secondary">
            {fileStatus.uploadProgress}% complete
          </Typography>
        </Paper>
      )}

      {/* Submit Button */}
      {fileStatus.file && !fileStatus.success && !fileStatus.isUploading && (
        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleUpload}
            startIcon={<CloudUploadIcon />}
            disabled={fileStatus.isUploading}
          >
            Submit for Processing
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default VenueDataImportView;
